import React, { createContext, useState, useEffect, useContext } from 'react';
import { PageContext } from './ContextPage';
import { DocumentContext } from './ContextDocument';
import './styles.css';

function updateOrAppendById(array, id, newData) {
  // Look for the index of the object with the given ID
  const index = array.findIndex((item) => item.id === id);

  if (index !== -1) {
    // ID not found: append the new object to the end of the array
    return [...array.slice(0, index), { ...array[index], ...newData }, ...array.slice(index + 1)];
  } else {
    // ID found: update the object with new data
    return [...array, { id, ...newData }];
  }
}

/**
 * Section handles the current page the section is in scope of, and updates
 * parent sections if applicable. If it is the head section, it updates the document sections state instead.
 *
 * @param {Object} props - The properties passed to the component.
 * @param {React.ReactNode} props.children - The child elements to be included in the section.
 * @param {string} props.id - The unique identifier of the section.
 * @param {string} props.name - The name of the section.
 *
 * @returns {React.Element} The SectionContext.Provider element with children wrapped inside.
 *
 * @example
 *  <Section id='section1' name='Introduction'>
 *    <Paragraph>Introductory text...</Paragraph>
 *  </Section>
 *
 * The Section component provides the following context values:
 * - head: a boolean indicating whether the section is the head section.
 * - updateSubSections: a function that takes a section object and updates the corresponding section in the sections state.
 */
export const SectionContext = createContext();

export function Section({ children, id, name }) {
  const { updateSections } = useContext(DocumentContext);
  const { pageNumber } = useContext(PageContext);
  const parent = useContext(SectionContext) ?? {};
  const [sections, setSections] = useState([]);

  const { head = true } = parent;

  const updateSubSections = ({ id, ...attrs }) => {
    setSections((prev) => {
      return updateOrAppendById(prev, id, attrs);
    });
  };

  useEffect(() => {
    if (!id) {
      console.error('Section requires an id');
    }
    if (head) {
      // update Document
      updateSections({ id, name, pageNumber: sections[0]?.pageNumber || 0, sections });
    } else {
      // update parent Section
      parent?.updateSubSections?.({ id, name, pageNumber, sections });
    }
  }, [id, name, pageNumber, sections]);

  const contextValue = {
    head: false,
    updateSubSections,
  };

  return <SectionContext.Provider value={contextValue}>{children}</SectionContext.Provider>;
}
